import React from 'react';
import { Order } from '../types';
import { formatCurrency } from '../util/formatCurrency';
import { formatDiscount, referralDiscountTerms } from '../util/referralDiscount';

interface ReferralDiscountCardProps {
  order: Order;
  canAttach: boolean;
  onRelease: () => void;
  onAttach: () => void;
}

const formatAttachedAt = (value: string) =>
  new Date(value).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const ReferralDiscountCard: React.FC<ReferralDiscountCardProps> = ({ order, canAttach, onRelease, onAttach }) => {
  const discount = order.referral_discount;
  const others = order.customer_referral_discounts ?? { available: 0, redeemed: 0 };
  const customerName = order.customer?.full_name ?? 'The customer';

  if (!discount) {
    if (!canAttach || order.status !== 'submitted' || others.available === 0) return null;

    return (
      <section
        aria-label="Referral Discount"
        className="bg-white shadow rounded-md p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm text-gray-700"
      >
        <p>
          <strong>Referral Discount:</strong> {customerName} has {others.available} referral {others.available === 1 ? 'discount' : 'discounts'} available.
        </p>
        <button type="button" onClick={onAttach} className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800 whitespace-nowrap">
          Attach referral discount
        </button>
      </section>
    );
  }

  const isReserved = discount.status === 'reserved';
  const isZeroFee = Number(order.tini_delivery_fee) === 0;
  const attachedBy = discount.attached_by
    ? `by ${discount.attached_by}${discount.attached_reason ? ` ("${discount.attached_reason}")` : ''}`
    : null;

  return (
    <section
      aria-label="Referral Discount"
      className="bg-white shadow rounded-md p-4 text-sm text-gray-700 border-l-4 border-green-600 space-y-3"
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-gray-900">Referral Discount</h2>
          {isReserved ? (
            <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full" style={{ background: '#fef3c7', color: '#92400e' }}>
              Reserved for this order
            </span>
          ) : (
            <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">Redeemed</span>
          )}
        </div>
        {isReserved && (
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400">Redeems when the order is marked Paid</span>
            <button type="button" onClick={onRelease} className="text-red-500 hover:text-red-700 text-sm whitespace-nowrap">
              Release discount
            </button>
          </div>
        )}
      </div>

      {isReserved && isZeroFee && (
        <p role="alert" className="bg-amber-50 border border-amber-200 rounded-md p-3 text-amber-800">
          The delivery fee is E0.00, so this discount takes nothing off yet. If the order stays at E0.00, release it before marking it paid so {customerName} can use it on a later order.
        </p>
      )}

      <dl className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <dt className="text-xs text-gray-500">Discount</dt>
          <dd>
            <strong>{referralDiscountTerms(discount, discount.capped)}</strong>: {formatDiscount(discount.amount)} of {formatCurrency(Number(order.tini_delivery_fee))}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">Earned from</dt>
          <dd>
            {discount.earned_from_customer_id ? (
              <a href={`/customers/${discount.earned_from_customer_id}`} className="text-blue-600 hover:underline">
                {discount.earned_from}
              </a>
            ) : (
              discount.earned_from
            )}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">Attached</dt>
          <dd>
            {discount.reserved_at ? formatAttachedAt(discount.reserved_at) : 'Unknown'}
            {attachedBy && ` ${attachedBy}`}
            {discount.moved_from_order_reference && (
              <span className="block text-xs text-gray-500">
                Moved from order {discount.moved_from_order_reference} (cancelled by the customer's choice)
              </span>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">Customer's other referral discounts</dt>
          <dd>
            {others.available} available, {others.redeemed} redeemed
          </dd>
        </div>
      </dl>
    </section>
  );
};

export default ReferralDiscountCard;
